import { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useCompetitorPrices, formatChecked } from "@/lib/competitorPrices";
import { Check, X, ArrowRight, Crown } from "lucide-react";

function Header() {
  const { t } = useI18n();
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100/60">
      <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
        <Link to="/"><img src="/logo.jpg" alt="Smartpriced" className="h-10" /></Link>
        <nav className="hidden lg:flex items-center gap-8">
          <Link to="/#features" className="text-[15px] font-medium text-slate-600 hover:text-[#1A3F6E] whitespace-nowrap">{t("landing.nav_features")}</Link>
          <Link to="/vergelijk" className="text-[15px] font-medium text-[#2CA8AC] whitespace-nowrap">Vergelijk</Link>
          <Link to="/#pricing" className="text-[15px] font-medium text-slate-600 hover:text-[#1A3F6E] whitespace-nowrap">{t("nav.pricing")}</Link>
          <Link to="/#faq" className="text-[15px] font-medium text-slate-600 hover:text-[#1A3F6E] whitespace-nowrap">FAQ</Link>
        </nav>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <Link to="/register"><Button className="bg-[#2CA8AC] hover:bg-[#248F92] rounded-full px-6">{t("hero.cta")}</Button></Link>
        </div>
      </div>
    </header>
  );
}

export default function ComparePage() {
  const { t } = useI18n();
  const live = useCompetitorPrices();

  const features = [
    { name: t("compare.feat.woo"), sp: true, slimste: true, prisync: true, competera: false, price2spy: true },
    { name: t("compare.feat.shops"), sp: true, slimste: false, prisync: false, competera: false, price2spy: false },
    { name: t("compare.feat.matching"), sp: true, slimste: false, prisync: true, competera: true, price2spy: false },
    { name: t("compare.feat.repricing"), sp: true, slimste: true, prisync: true, competera: true, price2spy: false },
    { name: t("compare.feat.b2b"), sp: true, slimste: false, prisync: false, competera: false, price2spy: false },
    { name: t("compare.feat.multilang"), sp: true, slimste: false, prisync: true, competera: true, price2spy: true },
    { name: t("compare.feat.affiliate"), sp: true, slimste: false, prisync: false, competera: false, price2spy: false },
    { name: t("compare.feat.frequency"), sp: true, slimste: true, prisync: true, competera: true, price2spy: true },
    { name: t("compare.feat.pdf"), sp: true, slimste: false, prisync: false, competera: false, price2spy: false },
    { name: t("compare.feat.gdpr"), sp: true, slimste: true, prisync: false, competera: false, price2spy: false },
    { name: t("compare.feat.belgian"), sp: true, slimste: false, prisync: false, competera: false, price2spy: false },
    { name: t("compare.feat.trial"), sp: t("compare.feat.trial_14"), slimste: t("compare.feat.trial_30"), prisync: t("compare.feat.trial_14"), competera: "Demo", price2spy: t("compare.feat.trial_14") },
  ];

  // Price table rows: our prices + competitor prices from the monthly crawl (fallback: built-in values)
  const FALLBACK_ROWS = [
    { n: 100, sp: "€45", other: "-", prisync: "$99 (±100)", p2s: "-" },
    { n: 500, sp: "-", other: "-", prisync: "-", p2s: "$39.95 (500 URLs)" },
    { n: 1000, sp: "€75", other: "€79", prisync: "$199 (±1.000)", p2s: "-" },
    { n: 2500, sp: "€75", other: "-", prisync: "-", p2s: "$157.95 (2.000 URLs)" },
    { n: 5000, sp: "€99", other: "-", prisync: "$399 (±5.000)", p2s: "-" },
    { n: 7500, sp: "-", other: "€199", prisync: "-", p2s: "-" },
    { n: 25000, sp: "€245", other: "€399", prisync: "-", p2s: "on_request" },
    { n: 50000, sp: "€729", other: "€759", prisync: "-", p2s: "on_request" },
    { n: 100000, sp: "-", other: "€1.199", prisync: "-", p2s: "on_request" },
  ];
  const cell = (comp, n) => {
    const p = comp?.plans?.find((x) => x.products === n);
    return p ? `${comp.currency}${String(p.other).replace(".", ",")}${p.label ? ` (${p.label})` : ""}` : null;
  };
  const priceRows = FALLBACK_ROWS.map((r) => {
    const ours = live ? (() => {
      for (const c of Object.values(live)) { const p = c.plans?.find((x) => x.products === r.n && x.sp != null); if (p) return `€${p.sp}`; }
      return r.sp;
    })() : r.sp;
    return {
      name: `${r.n.toLocaleString("nl-NL")} ${t("compare.products")}`,
      sp: ours,
      other: live ? (cell(live["slimsteprijs-nl"], r.n) || "-") : r.other,
      prisync: live ? (cell(live["prisync"], r.n) || "-") : r.prisync,
      p2s: live ? (cell(live["price2spy"], r.n) || (r.p2s === "on_request" ? t("compare.on_request") : "-")) : (r.p2s === "on_request" ? t("compare.on_request") : r.p2s),
    };
  });

  const vsCards = [
    { name: "SlimstePrijs.nl", slug: "slimsteprijs-nl", desc: t("vs.slimste.desc"), saving: t("vs.saving.75") },
    { name: "Prisync", slug: "prisync", desc: t("vs.prisync.desc"), saving: t("vs.saving.50") },
    { name: "Competera", slug: "competera", desc: t("vs.competera.desc"), saving: t("vs.saving.transparent") },
    { name: "Price2Spy", slug: "price2spy", desc: t("vs.price2spy.desc"), saving: t("vs.saving.60") },
    { name: "Omnia Retail", slug: "omnia-retail", desc: t("vs.omnia.desc"), saving: t("vs.saving.smb") },
    { name: "Paarly", slug: "paarly", desc: t("vs.paarly.desc"), saving: t("vs.saving.allsectors") },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="compare-page">
      <Header />
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="bg-[#85D8DB]/10 text-[#1A3F6E] border-[#2CA8AC]/30 mb-6"><Crown className="w-3.5 h-3.5 mr-1" />{t("compare.badge")}</Badge>
          <h1 className="text-[2.8rem] sm:text-[3.2rem] font-bold text-[#1A3F6E] leading-tight">{t("compare.title")}</h1>
          <p className="mt-5 text-lg text-slate-500 max-w-2xl mx-auto">{t("compare.subtitle")}</p>
        </div>
      </section>

      {/* Price Table */}
      <section className="pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-[#1A3F6E] mb-8">{t("compare.price_title")}</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead><tr className="border-b-2 border-slate-200">
                <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">{t("compare.products")}</th>
                <th className="py-4 px-4 text-sm font-semibold text-[#2CA8AC] bg-[#85D8DB]/5">Smartpriced</th>
                <th className="py-4 px-4 text-sm font-semibold text-slate-600">SlimstePrijs.nl</th>
                <th className="py-4 px-4 text-sm font-semibold text-slate-600">Prisync</th>
                <th className="py-4 px-4 text-sm font-semibold text-slate-600">Price2Spy</th>
              </tr></thead>
              <tbody>
                {priceRows.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="py-4 px-4 font-medium text-[#1A3F6E]">{row.name}</td>
                    <td className="py-4 px-4 text-center bg-[#85D8DB]/5"><span className="font-bold text-[#2CA8AC] font-mono-data text-lg">{row.sp}</span></td>
                    <td className="py-4 px-4 text-center text-slate-600 font-mono-data">{row.other}</td>
                    <td className="py-4 px-4 text-center text-slate-600 font-mono-data">{row.prisync}</td>
                    <td className="py-4 px-4 text-center text-slate-600 font-mono-data">{row.p2s}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-4">{t("compare.last_checked")}: {formatChecked(live?.["slimsteprijs-nl"]?.last_checked)}. {t("compare.check_note")}</p>
        </div>
      </section>

      {/* Feature Table */}
      <section className="py-20 px-6 bg-slate-50/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-[#1A3F6E] mb-8">{t("compare.feature_title")}</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead><tr className="border-b-2 border-slate-200">
                <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600"></th>
                <th className="py-4 px-3 text-sm font-semibold text-[#2CA8AC] bg-[#85D8DB]/5">Smartpriced</th>
                <th className="py-4 px-3 text-sm font-semibold text-slate-600">SlimstePrijs</th>
                <th className="py-4 px-3 text-sm font-semibold text-slate-600">Prisync</th>
                <th className="py-4 px-3 text-sm font-semibold text-slate-600">Competera</th>
                <th className="py-4 px-3 text-sm font-semibold text-slate-600">Price2Spy</th>
              </tr></thead>
              <tbody>
                {features.map((f, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-3 px-4 text-sm text-[#1A3F6E]">{f.name}</td>
                    {["sp", "slimste", "prisync", "competera", "price2spy"].map((key) => (
                      <td key={key} className={`py-3 px-3 text-center ${key === "sp" ? "bg-[#85D8DB]/5" : ""}`}>
                        {f[key] === true ? <Check className="w-5 h-5 text-[#2CA8AC] mx-auto" /> :
                         f[key] === false ? <X className="w-5 h-5 text-slate-300 mx-auto" /> :
                         <span className="text-xs text-slate-500">{f[key]}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-[#1A3F6E]">{t("compare.convinced")}</h2>
          <p className="mt-4 text-lg text-slate-500">{t("compare.convinced_sub")}</p>
          <Link to="/register"><Button size="lg" className="mt-8 bg-[#2CA8AC] hover:bg-[#248F92] text-lg px-10 h-14 rounded-full shadow-xl shadow-[#2CA8AC]/25">{t("hero.cta")} <ArrowRight className="w-5 h-5 ml-2" /></Button></Link>
        </div>
      </section>

      {/* VS Cards */}
      <section className="py-20 px-6 bg-slate-50/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-[#1A3F6E] mb-8">{t("compare.detailed")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vsCards.map((vs, i) => (
              <Link key={i} to={`/vergelijk/${vs.slug}`} className="group">
                <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-[#1A3F6E]">Smartpriced vs {vs.name}</h3>
                    <ArrowRight className="w-4 h-4 text-[#2CA8AC] group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-sm text-slate-500 mb-4">{vs.desc}</p>
                  <Badge className="bg-[#85D8DB]/10 text-[#2CA8AC] border-[#2CA8AC]/20">{vs.saving}</Badge>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-10 px-6 border-t border-slate-200">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <img src="/logo.jpg" alt="Smartpriced" className="h-8" />
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <Link to="/terms" className="hover:text-[#2CA8AC]">Voorwaarden</Link>
            <Link to="/privacy" className="hover:text-[#2CA8AC]">Privacy</Link>
            <span>SB Company</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
